import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const faqData = [
  { q: 'Comment savoir si le moteur de mon volet roulant est HS ?', a: 'Les signes qui indiquent un moteur défectueux sont : un volet qui ne réagit plus du tout à la commande, un bruit anormal (grincement, claquement), un moteur qui tourne dans le vide, ou un volet qui s\'arrête en cours de course. Notre diagnostic gratuit permet de confirmer l\'état du moteur.' },
  { q: 'Quel est le prix pour changer un moteur de volet roulant à Lyon ?', a: 'Le prix dépend du type de moteur (filaire, radio, solaire) et de la marque. Nous vous fournissons un devis gratuit et détaillé avant toute intervention. Comptez généralement entre 200 € et 500 € pour un remplacement complet, pièces et main-d\'œuvre incluses.' },
  { q: 'Remplacez-vous les moteurs de toutes les marques ?', a: 'Oui, nous intervenons sur les moteurs Somfy, Bubendorff, Profalux, Simu, Nice, Faver et bien d\'autres. Nous installons des moteurs compatibles avec votre installation existante.' },
];

export default function ReparationMoteur() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question', name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <>
      <SEOHead
        title="Réparation moteur de volet roulant à Lyon | DFPRO"
        description="Moteur de volet roulant HS à Lyon ? Remplacement toutes marques : Somfy, Bubendorff, Profalux… Filaire ou radio. Devis gratuit, garantie 10 ans. 09 80 80 36 84."
        canonicalUrl="https://depanfenetres-lyon.fr/reparation-moteur-volet-roulant-lyon/"
        jsonLd={faqJsonLd}
      />
      <Header />
      <section className="w-full">
        <img src="/images/dfpro-volet-moteur-2026.webp" alt="Remplacement de moteur de volet roulant à Lyon, toutes marques" className="w-full h-[200px] md:h-[300px] object-cover object-center" loading="lazy" />
      </section>


      <main className="pt-20">
        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-secondary-400 mb-6 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors">Accueil</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <Link to="/reparation-volet-roulant-lyon/" className="hover:text-primary-500 transition-colors">Volet roulant</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-secondary-500">Réparation moteur</span>
            </nav>
            <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-800">Réparation et remplacement de moteur de volet roulant à Lyon</h1>
            <p className="mt-4 text-sm md:text-lg text-secondary-500 max-w-3xl leading-relaxed">
              Moteur de volet roulant en panne ? Nos techniciens diagnostiquent et remplacent votre moteur à Lyon et dans la métropole. Toutes marques, garantie 10 ans.
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2 space-y-8">
                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Quand faut-il changer le moteur de son volet roulant ?</h2>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Le moteur est le cœur de votre volet roulant électrique. Avec le temps et l'usage (des milliers de cycles d'ouverture/fermeture), il peut s'user et finir par tomber en panne. Certains signes ne trompent pas et indiquent qu'il est temps de faire intervenir un professionnel pour un diagnostic.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Un moteur qui émet un bruit anormal — grincement, claquement, ronflement excessif — est souvent en fin de vie. De même, un volet qui s'arrête en cours de course sans raison apparente, ou qui fonctionne de manière irrégulière (parfois il monte, parfois non), signale un problème moteur. Enfin, si votre volet ne réagit plus du tout à la commande (interrupteur ou télécommande), le moteur est probablement hors service.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Moteur filaire ou radio : lequel choisir ?</h2>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Si vous devez remplacer votre moteur, vous avez le choix entre deux technologies principales. Le moteur filaire est la solution classique, fiable et économique. Il est commandé par un interrupteur mural relié au moteur par un câble électrique. C'est la solution la plus répandue dans les installations existantes.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Le moteur radio (sans fil) offre plus de confort : il se commande par télécommande, peut être intégré à un système domotique, et permet de programmer des scénarios (ouverture/fermeture à heures fixes). Nos techniciens vous conseillent sur le meilleur choix en fonction de votre installation et de vos besoins.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Marques de moteurs prises en charge</h2>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Nous intervenons sur l'ensemble des marques du marché : Somfy (leader de la motorisation de volet roulant), Bubendorff (spécialiste du volet roulant intégré), Profalux, Simu, Nice, Faver. Nous connaissons les spécificités de chaque marque et savons diagnostiquer rapidement l'origine de la panne.
                  </p>
                </div>
              </div>

              <aside className="space-y-6">
                <div className="bg-background-100 rounded-lg p-5 sticky top-24">
                  <h3 className="font-heading font-semibold text-sm text-foreground-800 mb-4">Services connexes</h3>
                  <nav className="space-y-1">
                    <Link to="/motorisation-volet-roulant-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Motorisation de volet</Link>
                    <Link to="/volet-roulant-electrique-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Volet électrique</Link>
                    <Link to="/volet-roulant-ne-remonte-plus-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Volet qui ne remonte plus</Link>
                    <Link to="/reparation-volet-roulant-lyon/" className="block text-sm text-primary-500 hover:text-primary-600 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors font-medium">← Tous nos services</Link>
                  </nav>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800 text-center mb-8">Questions fréquentes — moteur de volet roulant</h2>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner title="Moteur de volet roulant en panne à Lyon ?" subtitle="Remplacement rapide, toutes marques. Devis gratuit, garantie 10 ans." />
      </main>
      <Footer />
    </>
  );
}